import { Component, OnInit } from '@angular/core';
import { ExperimentalDesignTypeEnum } from '../../../shared/experimental.design/experimental-design-type.enum';
import { Principal } from '../../../shared';
import { StudyEntryService } from '../../../shared/study/service/study-entry.service';
import { StudyEditorContext } from '../../study-editor.context';
import { ExperimentalDesignDetails, StudyDetails } from '../../../shared/study/model/study-details.model';
import { ObservationVariable } from '../../../shared/model/observation-variable.model';
import { T } from '@angular/cdk/keycodes';
import { ADD_OPTIONAL_DESIGN_VARS_PERMISSIONS, DELETE_EXPERIMENTAL_DESIGN_PERMISSIONS, GENERATE_EXPERIMENTAL_DESIGN_PERMISSIONS, IMPORT_DESIGN_PERMISSIONS } from '../../../shared/auth/permissions';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { TreatmentVariable } from '../../../shared/study/model/treatment-variable.model';
import { TreatmentFactorLabelInstance, TreatmentFactorsData } from './treatment-factors.model';
import { ExperimentalDesignParameters } from './experimental-design.parameters.model';
import { ExperimentDesignService } from './experiment-design.service';
import { StudyInstanceService } from '../../../shared/study/service/study-instance.service';
import { AlertService } from '../../../shared/alert/alert.service';
import { ModalConfirmComponent } from '../../../shared/modal/modal-confirm.component';
import { TranslateService } from '@ngx-translate/core';
import { StudyEditorService } from '../../study-editor.service';
import { ChooseEnvironmentModalComponent } from '../../shared/modals/choose-environment-modal/choose-environment-modal.component';
import { debounceTime } from 'rxjs/operators';
import { Subject, Subscription } from 'rxjs';
import { ImportDesignModalComponent } from './import-design/import-design-modal/import-design-modal.component';
import { StudyInstanceItem } from '../../shared/study-instances-table-selector/study-instances-table-selector.component';
import { HELP_MANAGE_STUDIES_EXPERIMENTAL_DESIGN, HELP_MANAGE_STUDIES_GERMPLASM } from '../../../app.constants';
import { BreedingMethodFilterTypeEnum } from '../../../shared/breeding-methods-select/breeding-methods-select.component';
import { ExperimentalDesignContext } from './experimental-design-context';
import { StudyInstanceModel } from '../../../shared/dataset/model/study-instance.model';
import { EditTreatmentFactorsModalComponent } from '../../shared/modals/edit-treatment-factors-modal/edit-treatment-factors-modal.component';
import { CropParameterService } from '../../../shared/crop-parameter/service/crop-parameter.service';
import { CropParameterTypeEnum } from '../../../shared/crop-parameter/model/crop-parameter-type-enum';
import { ParamContext } from '../../../shared/service/param.context';

enum ExperimentalDesignConstants {
    ENTRY_NO = 8230,
    REP_NO = 8210,
    BLOCK_NO = 8220,
    PLOT_NO = 8200,
    ROW = 8581,
    COL = 8582,
    PREP = 8842
}

@Component({
    selector: 'jhi-experimental-design',
    templateUrl: './experimental-design.component.html',
    styleUrl: './experimental-design.component.scss'
})
export class ExperimentalDesignComponent implements OnInit {

    public readonly SPECIAL_FACTORS: { label: string, termId: number, name: string }[] = [
        { label: 'study.details.experimental-design.summary.treatment-factor', termId: ExperimentalDesignConstants.ENTRY_NO, name: 'ENTRY_NO' },
        { label: 'study.details.experimental-design.summary.plot-factor', termId: ExperimentalDesignConstants.PLOT_NO, name: 'PLOT_NO' },
        { label: 'study.details.experimental-design.summary.block-factor', termId: ExperimentalDesignConstants.BLOCK_NO, name: 'BLOCK_NO' },
        { label: 'study.details.experimental-design.summary.replicate-factor', termId: ExperimentalDesignConstants.REP_NO, name: 'REP_NO' },
        { label: 'study.details.experimental-design.summary.row-factor', termId: ExperimentalDesignConstants.ROW, name: 'ROW' },
        { label: 'study.details.experimental-design.summary.column-factor', termId: ExperimentalDesignConstants.COL, name: 'COL' },
        { label: '', termId: ExperimentalDesignConstants.PREP, name: 'PREP' }
    ];

    protected readonly DELETE_EXPERIMENTAL_DESIGN_PERMISSIONS = DELETE_EXPERIMENTAL_DESIGN_PERMISSIONS;
    protected readonly IMPORT_DESIGN_PERMISSIONS = IMPORT_DESIGN_PERMISSIONS;
    protected readonly ExperimentalDesignTypeEnum = ExperimentalDesignTypeEnum;

    studyDetails: StudyDetails;

    designTypes: ExperimentalDesignTypeEnum[];
    currentDesignType: ExperimentalDesignTypeEnum;
    data: ExperimentalDesignParameters = {};

    insertionManners: { id: string, name: string, description: string }[] = [];
    designTypeView = [];
    currentParams: any;
    entryDetailSettingsMap: Map<number, ObservationVariable> = new Map<number, ObservationVariable>();
    totalGermplasmEntryListCount: number;
    replicationsArrangementGroupsOpts: { 1: string; 2: string; 3: string; };
    germplasmNumberOfTestEntriesPerBlock: number;
    germplasmTotalCheckEntriesCount: number;
    germplasmTotalTestEntriesCount: number;
    germplasmNumberOfPlotsPerBlock: number;
    germplasmTotalNumberOfPlots: number;
    EXPERIMENTAL_DESIGN_PARTIALS_LOC: any;
    germplasmTotalNonReplicatedEntriesCounts: any;
    isGeneratedOwnDesign: boolean;
    noOfEnvironments: number;
    showAdvancedOptions: boolean;
    hideAdditionalDetailsPanel = true;
    goToTreatmentFactorsModal = false;

    augmentedParametersChangedSubject = new Subject<any>();
    augmentedParametersChanged$ = this.augmentedParametersChangedSubject.asObservable();
    augmentedParametersSubscription: Subscription;

    prepParametersChangedSubject = new Subject<any>();
    prepParametersChanged$ = this.prepParametersChangedSubject.asObservable();
    prepParametersSubscription: Subscription;

    chooseEnvironmentModal: NgbModalRef;
    isNotRandFirstRepWarningShown = false;
    firstRepNotRandomized: boolean;

    constructor(public principal: Principal,
                public studyEntryService: StudyEntryService,
                public studyEditorContext: StudyEditorContext,
                private experimentDesignService: ExperimentDesignService,
                private studyInstanceService: StudyInstanceService,
                private alertService: AlertService,
                public translateService: TranslateService,
                private studyEditorService: StudyEditorService,
                public modalService: NgbModal,
                public experimentalDesignContext: ExperimentalDesignContext,
                private cropParameterService: CropParameterService,
                protected paramContext: ParamContext) {
    }

    async ngOnInit(): Promise<void> {
        this.paramContext.readParams();
        this.cropParameterService.getCropParameter(this.paramContext.cropName, this.paramContext.programUUID, CropParameterTypeEnum.FIRST_REP_NOT_RANDOMIZED_SWITCH)
            .toPromise().then(cropParameter => {
                this.firstRepNotRandomized = Boolean(parseInt(cropParameter.value, 10));
            });

        this.studyDetails = this.studyEditorContext.studyDetails;

        const studyEntriesCount = await this.studyEntryService.getStudyEntriesCount(this.studyEditorContext.studyId);
        this.studyEditorContext.germplasmListExists = studyEntriesCount > 0;
        this.studyEditorContext.studyDetails.numberOfEntries = studyEntriesCount;

        const entryDetailsList = this.studyEditorContext.studyDetails.entryDetails;

        entryDetailsList.forEach(item => this.entryDetailSettingsMap.set(item.termId, item));

        // Transform the enum mapping into an array of objects
        this.designTypes = ExperimentalDesignTypeEnum.getValues();
        this.data = this.createExperimentalDesignParameters();
        this.currentDesignType = ExperimentalDesignTypeEnum.getById(this.data.designType);

        // Reset all alerts to avoid them being displayed multiple times after changing the design type
        this.alertService.closeAllAlerts();

        if (this.currentDesignType === ExperimentalDesignTypeEnum.RANDOMIZED_COMPLETE_BLOCK
            || this.currentDesignType === ExperimentalDesignTypeEnum.RESOLVABLE_INCOMPLETE_BLOCK
            || this.currentDesignType === ExperimentalDesignTypeEnum.ROW_COL) {
            // Only show the warning for applicable designs (RCBD, RIBD, ROW_COL)
            this.showFirstRepNotRandomizedWarning(this.firstRepNotRandomized && !this.isNotRandFirstRepWarningShown);
        }

        // This is to automatically refresh the design details for augmented design
        // whenever the Experimental tab is viewed
        if (this.data.designType === ExperimentalDesignTypeEnum.AUGMENTED_RANDOMIZED_BLOCK.id) {
            this.refreshDesignDetailsForAugmentedDesign();
        }
        if (this.data.designType === ExperimentalDesignTypeEnum.ENTRY_LIST_ORDER.id) {
            this.refreshDesignDetailsForELODesign();
        }
        if (this.data.designType === ExperimentalDesignTypeEnum.P_REP.id) {
            this.refreshDesignDetailsForPRepDesign();
        }

        this.generateDesignView();
        this.generateInsertionMannerView();

        this.totalGermplasmEntryListCount = this.studyEditorContext.studyDetails.numberOfEntries;
        this.noOfEnvironments = this.studyEditorContext.studyDetails.numberOfEnvironments;

        this.replicationsArrangementGroupsOpts = {
            1: 'In a single column',
            2: 'In a single row',
            3: 'In adjacent columns'
        };

        this.augmentedParametersSubscription = this.augmentedParametersChanged$.pipe(debounceTime(500)).subscribe(
            (params) => {
                this.refreshDesignDetailsForAugmentedDesign();
            }
        );

        this.prepParametersSubscription = this.prepParametersChanged$.pipe(debounceTime(500)).subscribe(
            (params) => {
                this.refreshDesignDetailsForPRepDesign();
            }
        );
    }

    private createExperimentalDesignParameters(): ExperimentalDesignParameters {

        let experimentalDesignParameters: ExperimentalDesignParameters = {};
        // Set default values:
        experimentalDesignParameters.startingPlotNo = 1;
        experimentalDesignParameters.checkStartingPosition = 1;
        experimentalDesignParameters.checkInsertionManner = '8414';
        experimentalDesignParameters.useLatenized = false;

        const experimentalDesignDetails: ExperimentalDesignDetails = this.studyEditorContext.studyDetails.experimentalDesignDetail;
        if (experimentalDesignDetails) {
            experimentalDesignParameters = {
                blockSize: this.getObservationVariableValue<number>(experimentalDesignDetails.blockSize),
                colsPerReplications: this.getObservationVariableValue<number>(experimentalDesignDetails.numberOfColsInReps),
                designType: ExperimentalDesignTypeEnum.getByTermId(experimentalDesignDetails.experimentalDesign?.value).id,
                useLatenized: ExperimentalDesignTypeEnum.getByTermId(experimentalDesignDetails.experimentalDesign?.value).latinized || false,
                nblatin: this.getObservationVariableValue<number>(experimentalDesignDetails.numberOfContiguousBlocksLatinize),
                nclatin: this.getObservationVariableValue<number>(experimentalDesignDetails.numberOfContiguousColsLatinize),
                nrlatin: this.getObservationVariableValue<number>(experimentalDesignDetails.numberOfContiguousRowsLatinize),
                replatinGroups: experimentalDesignDetails.numberOfRepsInCols?.value,
                numberOfBlocks: this.getObservationVariableValue<number>(experimentalDesignDetails.numberOfBlocks),
                replicationPercentage: this.getObservationVariableValue<number>(experimentalDesignDetails.replicationPercentage),
                replicationsCount: this.getObservationVariableValue<number>(experimentalDesignDetails.numberOfReplicates),
                rowsPerReplications: this.getObservationVariableValue<number>(experimentalDesignDetails.numberOfRowsInReps),
                startingPlotNo: experimentalDesignDetails.startingPlotNumber,
                checkSpacing: this.getObservationVariableValue<number>(experimentalDesignDetails.checksSpacing),
                checkStartingPosition: this.getObservationVariableValue<number>(experimentalDesignDetails.checksStartingPosition),
                checkInsertionManner: this.getObservationVariableValue<string>(experimentalDesignDetails.checksMannerOfInsertion),
                firstRepNotRandomized: this.firstRepNotRandomized
            };
            if (experimentalDesignDetails.replicationsMap && experimentalDesignDetails.replicationsMap.value) {
                const arrangementValue = parseInt(experimentalDesignDetails.replicationsMap.value, 10);
                const arrangementId = experimentalDesignDetails.replicationsMap.possibleValues.find((item: any) => item.id === arrangementValue).name;
                experimentalDesignParameters.replicationsArrangement = parseInt(arrangementId, 10);
            }
            this.showAdvancedOptions = experimentalDesignParameters.useLatenized;
        }

        experimentalDesignParameters.treatmentFactorsData = this.convertToTreatmentFactorsData(this.studyEditorContext.studyDetails.treatmentFactors);

        return experimentalDesignParameters;
    }

    getObservationVariableValue<T extends string | number>(observationVariable: ObservationVariable | null): T {
        if (observationVariable && observationVariable.value !== null && observationVariable.value !== undefined) {
            if (typeof observationVariable.value === 'string' && !isNaN(Number(observationVariable.value)) && (T as any) === Number) {
                return Number(observationVariable.value) as T;
            }
            return observationVariable.value as T;
        }
    }

    generateDesignView() {
        // Add Breeding View Engine Designs except for for Custom Design Import
        this.designTypes.forEach((index, designType: any) => {
            if (designType.name !== ExperimentalDesignTypeEnum.CUSTOM_IMPORT.name) {
                this.designTypeView.push(designType);
            }
        });
    };

    async generateInsertionMannerView() {
        this.insertionManners = await this.experimentDesignService.getInsertionManners().toPromise();
    };

    protected isDeleteDesignDisable() {
        return !this.principal.hasAnyAuthorityDirect(DELETE_EXPERIMENTAL_DESIGN_PERMISSIONS) || //
            !this.studyEditorContext.hasGeneratedDesign() || //
            this.studyEditorContext.hasListOrSubObs() || //
            this.studyEditorContext.hasMeansDataset(); //
    };

    deleteDesign() {
        const modalConfirmDelete = this.modalService.open(ModalConfirmComponent);
        modalConfirmDelete.componentInstance.message = this.translateService.instant('study-editor.experiment-design.delete-design.message');
        modalConfirmDelete.componentInstance.showButtonIcons = false;
        modalConfirmDelete.componentInstance.cancelLabel = this.translateService.instant('no');
        modalConfirmDelete.componentInstance.confirmLabel = this.translateService.instant('yes');
        modalConfirmDelete.result.then(() => {
            this.experimentDesignService.deleteDesign(this.studyEditorContext.studyId).toPromise().then(
                (response) => {
                    this.alertService.success('study-editor.experiment-design.delete-design.success');
                    this.studyEditorService.triggerRefreshAndWait().then(() => {
                        this.studyEditorService.navigateToTab(this.studyEditorContext.studyId, 'study-settings');
                    });
                }
            );
        });
    };

    disableDesignTypeSelect() {
        return !this.principal.hasAnyAuthorityDirect(GENERATE_EXPERIMENTAL_DESIGN_PERMISSIONS) || this.studyEditorContext.hasGeneratedDesign();
    };

    generateDesign() {
        if (!this.validateDesignTypeAndTreatmentFactors()) {
            // If Design is not RCBD and treatment factors are set, then we should not proceed with design generation
            return;
        }
        if (this.data.designType !== ExperimentalDesignTypeEnum.ENTRY_LIST_ORDER.id) {
            this.experimentDesignService.getBVDesignLicense().toPromise().then((license: any) => {
                // we need only the first license
                let licenseExpiryDays = license[0].expiryDays;
                if (licenseExpiryDays <= 0) {
                    this.alertService.error('study-editor.experiment-design.license-expired');
                } else if (licenseExpiryDays <= 30) {
                    this.showConfirmDialog(this.translateService.instant('study-editor.experiment-design.license-expiring', { param: licenseExpiryDays })).then(() => {
                        this.continueGeneration();
                    });
                } else {
                    this.continueGeneration();
                }
            });
        } else {
            this.continueGeneration();
        }
    };

    showConfirmDialog(message: string) {
        return new Promise((resolve, reject) => {
            const modalConfimRef = this.modalService.open(ModalConfirmComponent);
            modalConfimRef.componentInstance.message = message;
            modalConfimRef.result.then((result) => {
                resolve(result);
            }, (reason) => {
                reject(reason);
            });
        });
    };

    isImportedDesign() {
        let selectedExperimentDesignType = ExperimentalDesignTypeEnum.getById(this.data.designType);
        if (!selectedExperimentDesignType) {
            return true;
        }
        return this.data.designType != null
            && selectedExperimentDesignType.name === ExperimentalDesignTypeEnum.CUSTOM_IMPORT.name;
    };

    isBVDesign() {
        let selectedExperimentDesignType = ExperimentalDesignTypeEnum.getById(this.data.designType);
        if (!selectedExperimentDesignType) {
            return false;
        }
        return this.data.designType != null && selectedExperimentDesignType.name !== ExperimentalDesignTypeEnum.CUSTOM_IMPORT.name;
    };

    doValidate() {

        if (!this.validateStartingPlotNo()) {
            this.alertService.error('study-editor.experiment-design.validation.starting-plot-must-be-an-integer');
            return false;
        }

        switch (this.currentDesignType.id) {
        case ExperimentalDesignTypeEnum.RANDOMIZED_COMPLETE_BLOCK.id: {
            if (!this.data.replicationsCount) {
                this.alertService.error('study-editor.experiment.design.replication.count.rcbd.error');
                return false;
            }

            if (this.studyEditorContext.studyDetails.treatmentFactors && this.studyEditorContext.studyDetails.treatmentFactors.length && !this.data.treatmentFactorsData) {
                this.alertService.error('study-editor.experiment.design.no.treatment');
                return false;
            }

            if (this.data.treatmentFactorsData) {
                const treatmentFactorsData = this.data.treatmentFactorsData;
                for (const key of Object.keys(treatmentFactorsData)) {
                    if (!treatmentFactorsData[key].variableId) {
                        this.alertService.error('study-editor.treatment-factor.validation.choose-treatment-factor-label');
                        return false;
                    }
                    if (!treatmentFactorsData[key].levels || parseInt(treatmentFactorsData[key].levels, 10) <= 0) {
                        this.alertService.error('study-editor.treatment-factor.validation.number-of-treatments-must-be-greater-than-zero');
                        return false;
                    }

                    if (treatmentFactorsData[key].labels) {
                        for (let label of treatmentFactorsData[key].labels) {
                            if (label === null || label === '') {
                                this.alertService.error('study-editor.treatment-factor.validation.invalid-treatment-factor-label-values');
                                return false;
                            }
                        }
                    }

                    if (!treatmentFactorsData[key].variableId || treatmentFactorsData[key].variableId === 0) {
                        this.alertService.error('study-editor.treatment-factor.validation.invalid.unpaired');
                        return false;
                    }

                    if (treatmentFactorsData[treatmentFactorsData[key].variableId]) {
                        this.alertService.error('study-editor.treatment-factor.validation.invalid.used.paired');
                        return false;
                    }
                }
            }

            break;
        }
        case ExperimentalDesignTypeEnum.RESOLVABLE_INCOMPLETE_BLOCK.id: {

            if (!this.data.replicationsCount) {
                this.alertService.error('study-editor.experiment.design.replication.count.resolvable.error');
                return false;
            }

            if (!this.data.blockSize) {
                this.alertService.error('study-editor.experiment.design.block.size.should.be.a.greater.than.1');
                return false;
            }

            if (this.totalGermplasmEntryListCount % this.data.blockSize > 0) {
                this.alertService.error('study-editor.experiment.design.block.size.not.a.factor.of.treatment.size');
                return false;
            }

            // latinized
            if (this.data.useLatenized) {
                if (this.data.nblatin === null) {
                    this.alertService.error('study-editor.experiment.design.nblatin.should.be.greater.than.zero');
                    return false;
                }
                if (Number(this.data.nblatin) >= (this.totalGermplasmEntryListCount / this.data.blockSize)) {
                    this.alertService.error('study-editor.experiment.design.nblatin.should.not.be.greater.than.block.level');
                    return false;
                }

                if (Number(this.data.replicationsArrangement) <= 0) {
                    this.alertService.error('study-editor.experiment.design.select.field.arrangement');
                    return false;
                }
                if (Number(this.data.replicationsArrangement) === 3) {
                    if (!this.data.replatinGroups) {
                        this.alertService.error('study-editor.experiment.design.replatinGroups.invalid');
                        return false;
                    }

                    // validate sum of replatinGroups
                    let sum = 0;
                    let arrGroups = this.data.replatinGroups.split(',');

                    for (let i = 0; i < arrGroups.length; i++) {
                        sum += Number(arrGroups[i]);
                    }

                    if (sum !== Number(this.data.replicationsCount)) {
                        this.alertService.error('study-editor.experiment.design.replating.groups.not.equal.to.replicates');
                        return false;
                    }
                }
            }

            break;
        }
        case ExperimentalDesignTypeEnum.ROW_COL.id: {
            if (!this.data.replicationsCount) {
                this.alertService.error('study-editor.experiment.design.replication.count.resolvable.error');
                return false;
            }

            if (this.data.rowsPerReplications * this.data.colsPerReplications !== this.totalGermplasmEntryListCount) {
                this.alertService.error('study-editor.experiment.design.resolvable.incorrect.row.and.col.product.to.germplasm.size');
                return false;
            }

            if (this.data.useLatenized) {

                if (Number(this.data.nrlatin) >= Number(this.totalGermplasmEntryListCount) / Number(this.data.rowsPerReplications)) {
                    this.alertService.error('study-editor.experiment.design.nrlatin.should.be.less.than.ntreatments.over.nrows');
                    return false;
                }

                if (Number(this.data.nclatin) >= Number(this.totalGermplasmEntryListCount) / Number(this.data.colsPerReplications)) {
                    this.alertService.error('study-editor.experiment.design.nclatin.should.be.less.than.ntreatments.over.ncolumns');
                    return false;
                }

                if (Number(this.data.nrlatin) <= 0 || Number(this.data.nrlatin) >= Number(this.data.rowsPerReplications)) {
                    this.alertService.error('study-editor.experiment.design.nrlatin.should.be.less.than.rows.per.replication');
                    return false;

                }

                if (Number(this.data.nclatin) <= 0 || Number(this.data.nclatin) >= Number(this.data.colsPerReplications)) {
                    this.alertService.error('study-editor.experiment.design.nclatin.should.be.less.than.cols.per.replication');
                    return false;

                }

                if (Number(this.data.replicationsArrangement <= 0)) {
                    this.alertService.error('study-editor.experiment.design.select.field.arrangement');
                    return false;
                }

                if (Number(this.data.replicationsArrangement) === 3) {
                    if (!this.data.replatinGroups) {
                        this.alertService.error('study-editor.experiment.design.replatinGroups.invalid');
                        return false;
                    }

                    // validate sum of replatinGroups
                    let _sum = 0;
                    let _arrGroups = this.data.replatinGroups.split(',');

                    for (let j = 0; j < _arrGroups.length; j++) {
                        _sum += Number(_arrGroups[j]);
                    }

                    if (_sum !== Number(this.data.replicationsCount)) {
                        this.alertService.error('study-editor.experiment.design.replating.groups.not.equal.to.replicates');
                        return false;
                    }
                }

            }

            break;
        }
        case ExperimentalDesignTypeEnum.AUGMENTED_RANDOMIZED_BLOCK.id: {

            if (!this.validateNumberOfBlocks()) {
                return false;
            }
            if (!this.checkIfTheNumberOfTestEntriesPerBlockIsWholeNumber()) {
                return false;
            }
            if (!this.validateNumberOfChecks()) {
                return false;
            }
            break;

        }
        case ExperimentalDesignTypeEnum.ENTRY_LIST_ORDER.id: {

            if (this.germplasmTotalCheckEntriesCount > 0) {
                if (this.germplasmTotalTestEntriesCount === 0) {
                    this.alertService.error('study-editor.germplasm.list.all.entries.can.not.be.checks');
                    return false;
                }
                if (this.data.checkStartingPosition > this.germplasmTotalTestEntriesCount) {
                    this.alertService.error('study-editor.germplasm.list.start.index.less.than.germplasm.error');
                    return false;
                }
                if (this.data.checkStartingPosition < 1) {
                    this.alertService.error('study-editor.germplasm.list.starting.index.should.be.greater.than.zero');
                    return false;
                }
                if (this.data.checkSpacing && this.data.checkSpacing > this.germplasmTotalTestEntriesCount) {
                    this.alertService.error('study-editor.germplasm.list.spacing.less.than.germplasm.error');
                    return false;
                }
                if (!this.data.checkSpacing || this.data.checkSpacing < 1) {
                    this.alertService.error('study-editor.germplasm.list.number.of.rows.between.insertion.should.be.greater.than.zero');
                    return false;
                }
            } else {
                this.data.checkSpacing = null;
            }
            break;
        }
        case ExperimentalDesignTypeEnum.P_REP.id: {

            let largestPossiblePercentage = Math.round(((this.germplasmTotalTestEntriesCount - 1) / this.germplasmTotalTestEntriesCount) * 100);

            // Percentage should be below largestPossiblePercentage. There must be some unreplicated treatments in a partially replicated design.
            if ((!this.data.replicationPercentage && this.data.replicationPercentage !== 0)
                    || this.data.replicationPercentage > largestPossiblePercentage) {
                this.alertService.error('study-editor.experiment.design.replication.percentage.error', { param: largestPossiblePercentage });
                return false;
            }

            if (!this.data.replicationsCount) {
                this.alertService.error('study-editor.experiment.design.replication.count.rcbd.error');
                return false;
            }
            if (!this.data.replicationsCount) {
                this.alertService.error('study-editor.experiment.design.replication.count.should.be.greater.than.one');
                return false;
            }

            if (!this.data.blockSize) {
                this.alertService.error('study-editor.experiment.design.block.size.should.be.a.greater.than.1');
                return false;
            }

            if (this.germplasmNumberOfPlotsPerBlock % 1 !== 0) {
                this.alertService.error('study-editor.experiment.design.block.size.must.be.a.factor.of.the.number.of.treatment');
                return false;
            }

        }

        }

        if (!this.studyEditorContext.hasGermplasmList()) {
            this.alertService.error('study-editor.experiment.design.no.germplasm.list');
            return false;
        }

        return true;
    };

    validateStartingPlotNo() {
        let validNo = '^(?=.*[1-9].*)[0-9]{1,8}$';

        if (!String(this.data.startingPlotNo).match(validNo)) {
            return false;
        }
        return true;
    };

    checkIfTheNumberOfTestEntriesPerBlockIsWholeNumber() {
        // Check if the Number of Test entries per block is a whole number
        if (this.germplasmNumberOfTestEntriesPerBlock % 1 !== 0) {
            this.alertService.error('study-editor.experiment.design.number.of.test.entries.must.be.divisible');
            return false;
        }
        return true;
    };

    showOnlyIfNumberOfBlocksIsSpecified() {

        if (this.currentDesignType && this.currentDesignType.id === ExperimentalDesignTypeEnum.AUGMENTED_RANDOMIZED_BLOCK.id) {
            if (!this.data.numberOfBlocks && this.data.numberOfBlocks !== 0) {
                return false;
            }
            return true;
        }

    };

    showOnlyIfNumberOfBlockSizeIsSpecified() {

        if (this.currentDesignType && this.currentDesignType.id === ExperimentalDesignTypeEnum.P_REP.id) {
            if (!this.data.blockSize && this.data.blockSize !== 0) {
                return false;
            }
            return true;
        }

    };

    showOnlyIfNumberOfReplicationsCountIsSpecified() {

        if (this.currentDesignType && this.currentDesignType.id === ExperimentalDesignTypeEnum.P_REP.id) {
            if (!this.data.replicationsCount && this.data.replicationsCount !== 0) {
                return false;
            }
            if (isNaN(this.germplasmTotalNumberOfPlots)) {
                return false;
            }
            return true;
        }
    };

    refreshDesignDetailsForAugmentedDesign() {
        this.studyEntryService.getStudyEntriesMetadata(this.studyEditorContext.studyId).toPromise().then((metadata) => {
            this.germplasmTotalTestEntriesCount = metadata.testEntriesCount;
            this.germplasmTotalCheckEntriesCount = this.totalGermplasmEntryListCount - this.germplasmTotalTestEntriesCount;
            this.germplasmNumberOfTestEntriesPerBlock = this.germplasmTotalTestEntriesCount / this.data.numberOfBlocks;
            this.germplasmNumberOfPlotsPerBlock = this.germplasmNumberOfTestEntriesPerBlock + this.germplasmTotalCheckEntriesCount;
            this.germplasmTotalNumberOfPlots = this.germplasmNumberOfPlotsPerBlock * this.data.numberOfBlocks;
        });

    }

    refreshDesignDetailsForELODesign() {
        this.studyEntryService.getStudyEntriesMetadata(this.studyEditorContext.studyId).toPromise().then((metadata) => {
            this.germplasmTotalTestEntriesCount = metadata.testEntriesCount;
            this.germplasmTotalCheckEntriesCount = this.totalGermplasmEntryListCount - this.germplasmTotalTestEntriesCount;
        });
    };

    refreshDesignDetailsForPRepDesign() {
        this.studyEntryService.getStudyEntriesMetadata(this.studyEditorContext.studyId).toPromise().then((metadata) => {
            this.germplasmTotalNonReplicatedEntriesCounts = metadata.nonReplicatedEntriesCount;
            this.germplasmTotalCheckEntriesCount = metadata.nonTestEntriesCount - this.germplasmTotalNonReplicatedEntriesCounts - metadata.sisterLinesEntriesCount;
            this.germplasmTotalTestEntriesCount = this.totalGermplasmEntryListCount - this.germplasmTotalCheckEntriesCount - this.germplasmTotalNonReplicatedEntriesCounts;
            let noOfTestEntriesToReplicate = Math.round(this.germplasmTotalTestEntriesCount * (this.data.replicationPercentage / 100));
            this.germplasmTotalNumberOfPlots = (this.germplasmTotalTestEntriesCount - noOfTestEntriesToReplicate) +
                (noOfTestEntriesToReplicate * this.data.replicationsCount) +
                (this.germplasmTotalCheckEntriesCount * this.data.replicationsCount);
            this.germplasmNumberOfPlotsPerBlock = this.germplasmTotalNumberOfPlots / this.data.blockSize;
        });
    }

    showParamsWhenChecksAreSelected(designTypeId) {
        return !(designTypeId === ExperimentalDesignTypeEnum.ENTRY_LIST_ORDER.id &&
            (this.germplasmTotalTestEntriesCount === this.totalGermplasmEntryListCount
                || this.totalGermplasmEntryListCount === 0 || this.totalGermplasmEntryListCount === null));
    };

    validateNumberOfBlocks() {
        if (!this.data.numberOfBlocks) {
            this.alertService.error('study-editor.experiment.design.please.specify.blocks');
            return false;
        }
        return true;
    }

    validateNumberOfChecks() {

        if (this.germplasmTotalCheckEntriesCount === 0) {
            this.alertService.error('study-editor.experiment.design.please.specify.checks');
            return false;
        }
        return true;
    }

    continueGeneration() {
        if (!this.doValidate()) {
            return;
        }

        let experimentDesignInput: ExperimentalDesignParameters = { ...this.data };

        this.openSelectEnvironmentToGenerateModal(experimentDesignInput);
    };

    openSelectEnvironmentToGenerateModal(experimentDesignInput: ExperimentalDesignParameters) {
        this.studyInstanceService.listStudyInstances(this.studyEditorContext.studyId).toPromise().then((instances) => {
            let hasStudyInstanceForGeneration = false;
            instances.forEach((instance) => {
                if (instance.canBeDeleted) {
                    hasStudyInstanceForGeneration = true;
                }
            });

            const studyInstancesItems: StudyInstanceItem[] = instances.map((studyInstanceModel) => {
                return {
                    ...studyInstanceModel,
                    isChecked: !studyInstanceModel.hasExperimentalDesign
                };
            });

            if (hasStudyInstanceForGeneration) {
                let subscription;
                this.chooseEnvironmentModal = this.modalService.open(ChooseEnvironmentModalComponent as Component, { size: 'md', backdrop: 'static' });
                this.chooseEnvironmentModal.componentInstance.selectAll = studyInstancesItems.every((item) => item.isChecked);
                this.chooseEnvironmentModal.componentInstance.studyInstanceItems = studyInstancesItems.filter((item) => item.canBeDeleted);
                this.chooseEnvironmentModal.componentInstance.experimentDesignInput = experimentDesignInput;
                this.chooseEnvironmentModal.componentInstance.titleKey = 'study-editor.experiment-design.choose-environment.title';
                this.chooseEnvironmentModal.componentInstance.headerKey = 'study-editor.experiment-design.choose-environment.header';
                this.chooseEnvironmentModal.componentInstance.descriptionKey = 'study-editor.experiment-design.choose-environment.message';
                this.chooseEnvironmentModal.componentInstance.cancelLabelKey = 'cancel';
                this.chooseEnvironmentModal.componentInstance.continueLabelKey = 'study-editor.experiment-design.choose-environment.button.generate';
                this.chooseEnvironmentModal.componentInstance.hideEditTreatmentFactorsBtn = false;

                subscription = this.chooseEnvironmentModal.componentInstance.onContinue$.subscribe((params) => {
                    this.goToTreatmentFactorsModal = params['goToEditTreatmentFactors'];
                    this.validateSelectedEnvironments(params['selectedStudyInstanceItems'], experimentDesignInput);
                });

                this.chooseEnvironmentModal.result.then((result) => {
                }, (reason) => {
                    if (subscription) {
                        subscription.unsubscribe();
                    }
                });
            } else {
                this.alertService.warning('study-editor.experiment-design.cannot-be-regenerated');
            }
        });
    };

    validateSelectedEnvironments(studyInstanceItems: StudyInstanceItem[], experimentDesignInput: ExperimentalDesignParameters) {

        if (studyInstanceItems.length === 0) {
            this.alertService.error('study-editor.experiment-design.choose-environment.no-study-instance-selected');
            return;
        }

        experimentDesignInput.trialInstancesForDesignGeneration = studyInstanceItems.map((instance) => instance.instanceNumber);

        const environmentsWithMeasurements = [];
        //Check if selected instances has measurement data
        studyInstanceItems.forEach((instance) => {
            if (instance.isChecked && instance.instanceNumber && (instance.hasMeasurements || instance.hasFieldLayout)) {
                environmentsWithMeasurements.push(instance.instanceNumber);
            }
        });
        if (environmentsWithMeasurements.length > 0) {
            this.showHasMeasurementsWarning(experimentDesignInput, environmentsWithMeasurements, studyInstanceItems);
        } else {
            const environmentsWithDesign = [];
            //Check if selected instances has measurement data
            studyInstanceItems.forEach((instance) => {
                if (instance.isChecked && instance.instanceNumber && instance.hasExperimentalDesign) {
                    environmentsWithDesign.push(instance.instanceNumber);
                }
            });
            if (environmentsWithDesign.length > 0) {
                this.showHasGeneratedDesignWarning(experimentDesignInput, environmentsWithDesign, studyInstanceItems);
            } else {
                this.processTreatmentFactorsModal(experimentDesignInput, studyInstanceItems);
            }
        }
    };

    processTreatmentFactorsModal(experimentDesignInput: ExperimentalDesignParameters, studyInstanceItems: StudyInstanceItem[]) {
        if (this.goToTreatmentFactorsModal) {
            this.experimentalDesignContext.studyDetails = this.studyDetails;
            this.experimentalDesignContext.entryDetailSettingsMap = this.entryDetailSettingsMap;
            this.experimentalDesignContext.totalGermplasmEntryListCount = this.totalGermplasmEntryListCount;
            const treatmentFactorsModal = this.modalService.open(EditTreatmentFactorsModalComponent as Component, { size: 'xl', backdrop: 'static' });
            treatmentFactorsModal.componentInstance.studyInstanceItems = studyInstanceItems;
            treatmentFactorsModal.componentInstance.experimentDesignInput = experimentDesignInput;
            treatmentFactorsModal.result.then(() => {
                experimentDesignInput.excludedtreatmentFactors = this.experimentalDesignContext.excludedtreatmentFactors;

                return this.executeGenerateDesign(experimentDesignInput);
            }, () => {
                this.goToTreatmentFactorsModal = false;
            });
        } else {
            return this.executeGenerateDesign(experimentDesignInput);
        }
    };

    executeGenerateDesign(experimentDesignInput: ExperimentalDesignParameters) {
        this.chooseEnvironmentModal.componentInstance.isSaving = true;
        return this.experimentDesignService.generateDesign(this.studyEditorContext.studyId, experimentDesignInput).toPromise().then(
            (response) => {
                this.chooseEnvironmentModal.close();
                if (this.hasAddOptionalVariablePermission()) {
                    const modalConfirm = this.modalService.open(ModalConfirmComponent);
                    modalConfirm.componentInstance.message = this.translateService.instant('study-editor.experiment.design.generated.successfully.additional-details.confirm');
                    modalConfirm.componentInstance.cancelLabel = this.translateService.instant('no');
                    modalConfirm.componentInstance.confirmLabel = this.translateService.instant('yes');
                    modalConfirm.result.then(() => {
                        this.studyEditorService.triggerRefreshAndWait().then(() => {
                            this.hideAdditionalDetailsPanel = false;
                            this.studyInstanceService.listStudyInstances(this.studyEditorContext.studyId).toPromise().then((instances) => {
                                const updatedInstancesWithDesign = instances.filter((instance) => instance.hasExperimentalDesign);

                                // environment selection
                                const allEnvironments = new StudyInstanceModel();
                                allEnvironments.instanceId = -1;
                                allEnvironments.locationName = this.translateService.instant('study-editor.all-environments');
                                this.experimentalDesignContext.datasetInstances = [allEnvironments, ...updatedInstancesWithDesign];
                            });
                        });
                    }, () => {
                        this.studyEditorService.triggerRefreshAndWait().then(() => {
                            this.studyEditorService.navigateToDatasetTab(this.studyEditorContext.studyId, this.studyEditorContext.getPlotDataset().datasetId);
                        });
                    });
                } else {
                    this.alertService.success('study-editor.experiment.design.generated.successfully');
                    this.studyEditorService.triggerRefreshAndWait().then(() => {
                        this.studyEditorService.navigateToDatasetTab(this.studyEditorContext.studyId, this.studyEditorContext.getPlotDataset().datasetId);
                    });
                }
            }, (errResponse) => {
                const errorMessage = errResponse.error.errors[0].message;
                this.alertService.error('study-editor.experiment.design.generated.failed', { param: errorMessage });
            }).finally(() => this.chooseEnvironmentModal.componentInstance.isSaving = false);
    };

    showHasMeasurementsWarning(experimentDesignInput: ExperimentalDesignParameters, environmentsWithMeasurements: number[], studyInstanceItems: StudyInstanceItem[]) {
        const modalConfirm = this.modalService.open(ModalConfirmComponent);
        modalConfirm.componentInstance.message = this.translateService.instant('study-editor.experiment-design.choose-environment.has-measurement-warning', { param: environmentsWithMeasurements.join(', ') });
        modalConfirm.componentInstance.showButtonIcons = false;
        modalConfirm.componentInstance.cancelLabel = this.translateService.instant('no');
        modalConfirm.componentInstance.confirmLabel = this.translateService.instant('yes');
        modalConfirm.result.then(() => {
            this.processTreatmentFactorsModal(experimentDesignInput, studyInstanceItems).then(() => {
                modalConfirm.dismiss();
            });
        }, () => {
            modalConfirm.dismiss();
        });
    };


    onFirstRepNotRandomizedChange() {
        this.showFirstRepNotRandomizedWarning(!this.isNotRandFirstRepWarningShown && this.data.firstRepNotRandomized);
    }

    showFirstRepNotRandomizedWarning(isShowWarning: boolean) {
        if (isShowWarning) {
            this.isNotRandFirstRepWarningShown = true;
            this.alertService.warning('study-editor.experiment-design.first.rep.not.randomized.warning');
        }
    }

    showHasGeneratedDesignWarning(experimentDesignInput: ExperimentalDesignParameters, environmentsWithDesign: number[], studyInstanceItems: StudyInstanceItem[]) {
        const modalConfirm = this.modalService.open(ModalConfirmComponent);
        modalConfirm.componentInstance.message = this.translateService.instant('study-editor.experiment-design.choose-environment.has-design-warning', { param: environmentsWithDesign.join(', ') });
        modalConfirm.componentInstance.showButtonIcons = false;
        modalConfirm.componentInstance.cancelLabel = this.translateService.instant('no');
        modalConfirm.componentInstance.confirmLabel = this.translateService.instant('yes');
        modalConfirm.result.then(() => {
            this.processTreatmentFactorsModal(experimentDesignInput, studyInstanceItems).then(() => {
                modalConfirm.dismiss();
            });
        }, () => {
            modalConfirm.dismiss();
        });
        ;
    };

    private convertToTreatmentFactorsData(treatmentFactors: TreatmentVariable[]): TreatmentFactorsData {
        if (treatmentFactors) {
            const treatmentFactorsDataMap: TreatmentFactorsData = {};
            treatmentFactors.forEach((factor, index) => {
                if (factor.valueVariable.dataTypeCode === 'C' && (factor.categoricalValues == null || factor.categoricalValues.length === 0) ) {
                    factor.categoricalValues = factor.valueVariable?.possibleValues?.map((possibleValue) => {
                        return { id: String(possibleValue.id), name: possibleValue.name };
                    });
                    factor.categoricalValuesMap = new Map(
                        factor.categoricalValues?.map(item => [item.id, item.name])
                    );
                }

                treatmentFactorsDataMap[`${factor.levelVariable.termId}`] = {
                    levels: factor.values.length.toString() || '',
                    labels: factor.values || [],
                    alias: factor.levelVariable.name,
                    variableId: factor.valueVariable.termId,
                    labelAlias: factor.valueVariable.name,
                    categoricalValues: factor.categoricalValues
                };
            });
            return treatmentFactorsDataMap;
        }
        return null;
    }

    validateDesignTypeAndTreatmentFactors() {
        if (ExperimentalDesignTypeEnum.RANDOMIZED_COMPLETE_BLOCK.id !== this.data.designType
            && this.studyDetails.treatmentFactors.length > 0) {
            this.alertService.error('study-editor.experiment.design.type.not.rcb.treatment.factors.exist');
            return false;
        }
        return true;
    }

    onChangeDesignType($event: any) {
        this.data = this.createExperimentalDesignParameters();
        this.data.designType = $event.id;

        // Reset all alerts to avoid them being displayed multiple times after changing the design type
        this.alertService.closeAllAlerts();

        if (ExperimentalDesignTypeEnum.RANDOMIZED_COMPLETE_BLOCK.id === this.data.designType
        || ExperimentalDesignTypeEnum.RESOLVABLE_INCOMPLETE_BLOCK.id === this.data.designType
        || ExperimentalDesignTypeEnum.ROW_COL.id === this.data.designType) {
            this.data.firstRepNotRandomized = this.firstRepNotRandomized;
            this.showFirstRepNotRandomizedWarning(this.firstRepNotRandomized);
            if (!this.data.firstRepNotRandomized) {
                this.isNotRandFirstRepWarningShown = false;
            }
        }

        this.validateDesignTypeAndTreatmentFactors();

        this.currentDesignType = $event;

        if (ExperimentalDesignTypeEnum.ENTRY_LIST_ORDER.id === this.data.designType) {
            this.refreshDesignDetailsForELODesign();
        } else if (ExperimentalDesignTypeEnum.AUGMENTED_RANDOMIZED_BLOCK.id === this.data.designType) {
            this.refreshDesignDetailsForAugmentedDesign();
        } else if (ExperimentalDesignTypeEnum.P_REP.id === this.data.designType) {
            this.refreshDesignDetailsForPRepDesign();
        }
    }

    hasGeneratedDesign() {
        return this.studyEditorContext.hasGeneratedDesign();
    }

    getSpecialFactorsByDesignType() {
        const excludeTermIdsMap: { [key: number]: number[] } = {
            [ExperimentalDesignTypeEnum.RANDOMIZED_COMPLETE_BLOCK.id]: [
                ExperimentalDesignConstants.ENTRY_NO, ExperimentalDesignConstants.BLOCK_NO, ExperimentalDesignConstants.ROW, ExperimentalDesignConstants.COL, ExperimentalDesignConstants.PREP],
            [ExperimentalDesignTypeEnum.RESOLVABLE_INCOMPLETE_BLOCK.id]: [
                ExperimentalDesignConstants.ROW, ExperimentalDesignConstants.COL, ExperimentalDesignConstants.PREP],
            [ExperimentalDesignTypeEnum.ROW_COL.id]: [
                ExperimentalDesignConstants.BLOCK_NO, ExperimentalDesignConstants.PLOT_NO, ExperimentalDesignConstants.PREP],
            [ExperimentalDesignTypeEnum.AUGMENTED_RANDOMIZED_BLOCK.id]: [
                ExperimentalDesignConstants.REP_NO, ExperimentalDesignConstants.ROW, ExperimentalDesignConstants.COL, ExperimentalDesignConstants.PREP],
            [ExperimentalDesignTypeEnum.ENTRY_LIST_ORDER.id]: [
                ExperimentalDesignConstants.REP_NO, ExperimentalDesignConstants.BLOCK_NO, ExperimentalDesignConstants.ROW, ExperimentalDesignConstants.COL, ExperimentalDesignConstants.PREP],
            [ExperimentalDesignTypeEnum.P_REP.id]: [
                ExperimentalDesignConstants.REP_NO, ExperimentalDesignConstants.ROW, ExperimentalDesignConstants.COL, ExperimentalDesignConstants.PREP]
        };

        const excludeTermIds = excludeTermIdsMap[this.currentDesignType.id] || [];
        return this.SPECIAL_FACTORS.filter(item => !excludeTermIds.includes(item.termId));
    }

    importDesign() {
        this.modalService.open(ImportDesignModalComponent as Component, { size: 'md', backdrop: 'static' });
    }

    hasAddOptionalVariablePermission(): boolean {
        return this.principal.hasAnyAuthorityDirect(this.ADD_OPTIONAL_DESIGN_VARS_PERMISSIONS);
    }

    protected readonly ADD_OPTIONAL_DESIGN_VARS_PERMISSIONS = ADD_OPTIONAL_DESIGN_VARS_PERMISSIONS;
    protected readonly GENERATE_EXPERIMENTAL_DESIGN_PERMISSIONS = GENERATE_EXPERIMENTAL_DESIGN_PERMISSIONS;
    protected readonly HELP_MANAGE_STUDIES_EXPERIMENTAL_DESIGN = HELP_MANAGE_STUDIES_EXPERIMENTAL_DESIGN;
    protected readonly HELP_MANAGE_STUDIES_GERMPLASM = HELP_MANAGE_STUDIES_GERMPLASM;
    protected readonly BreedingMethodFilterTypeEnum = BreedingMethodFilterTypeEnum;
    protected readonly Array = Array;
}
